import {
  Button,
  makeStyles,
  Slider,
  useMediaQuery,
  useTheme,
  withStyles,
} from '@material-ui/core';
import React, { useLayoutEffect, useRef } from 'react';
import { QKey, useAnswerContext } from '../../../context';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

export const useStyles = makeStyles((theme) => ({
  header: {
    fontWeight: 500,
    color: 'rgb(32,33,36)',
    position: 'relative',
    zIndex: 100,
    maxWidth: 500,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: 28,
      lineHeight: '40px',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: 38,
      letterSpacing: -0.43,
      lineHeight: '52px',
    },
  },
  button: {
    background: '#e8f0fe',
    color: '#1967d2',
    '&:hover': {
      background: '#e8f0fe',
    },
    [theme.breakpoints.down('sm')]: {
      marginRight: 20,
      flex: 1,
    },
    [theme.breakpoints.up('sm')]: {
      marginRight: 50,
    },
  },
  info: {
    fontSize: 18,
    letterSpacing: 0.4,
    lineHeight: '52px',
    position: 'relative',
    zIndex: 100,
    fontWeight: 400,
    color: 'rgb(95,99,104)',
    maxWidth: 600,
    margin: 0,
  },
  selectContainer: {
    margin: '30px 0 10px',
    maxWidth: 600,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '10px',
  },
  buttonAnswer: {
    marginBottom: 20,
    background: '#fff',
    textAlign: 'left',
    justifyContent: 'flex-start',
    zIndex: 20,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: '9px 18px',
    },
    [theme.breakpoints.up('sm')]: {
      padding: '12px 24px',
      maxWidth: 440,
      marginRight: 20,
    },
    [theme.breakpoints.up('md')]: {
      padding: '12px 24px',
      marginRight: 20,
    },
    color: '#1a73e8',
    fontSize: 16,
    border: '1px solid #dadce0',
    lineHeight: '24px',
    fontWeight: 500,
    borderRadius: 4,
    '&:focus': {
      boxShadow: 'rgb(26 115 232) 0px 0px 0px 2px',
    },
    '&:hover': {
      background: '#fff',
      borderColor: '#1a73e8',
      boxShadow:
        'rgb(60 64 67 / 30%) 0px 1px 2px 0px, rgb(60 64 67 / 15%) 0px 1px 3px 1px',
    },
  },
  choosenButtonAnswer: {
    marginBottom: 20,
    textAlign: 'left',
    justifyContent: 'flex-start',
    background: '#f2f7fe',
    color: '#1a73e8',
    fontSize: 16,
    border: '1px solid #1a73e8',
    lineHeight: '24px',
    fontWeight: 500,
    borderRadius: 4,
    zIndex: 20,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: '9px 18px',
    },
    [theme.breakpoints.up('sm')]: {
      maxWidth: 440,
      padding: '12px 24px',
      marginRight: 20,
    },
    [theme.breakpoints.up('md')]: {
      padding: '12px 24px',
      marginRight: 20,
    },
    '&:hover': {
      borderColor: '#1a73e8',
      boxShadow:
        'rgb(60 64 67 / 30%) 0px 1px 2px 0px, rgb(60 64 67 / 15%) 0px 1px 3px 1px',
    },
    '&:focus': {
      boxShadow: 'rgb(26 115 232) 0px 0px 0px 2px',
    },
  },
  listItem: {
    border: '1px solid #ff0000',
  },
}));

const CustomSlider = withStyles({
  rail: {
    height: 6,
    borderRadius: 3,
  },
  root: {
    marginTop: 50,
    marginBottom: 50,
    height: 6,
    borderRadius: 3,
    maxWidth: 434,
    position: 'relative',
    zIndex: 100,
  },
  track: {
    height: 6,
    borderRadius: 3,
  },
  valueLabel: {
    left: 'calc(-50% - 8px)',
    height: 20,
    fontSize: '18px',
  },
  mark: {
    backgroundColor: '#1a73e8;',
    height: 6,
    width: 6,
    borderRadius: 3,
    marginTop: 0,
    marginLeft: -3,
  },
  markActive: {
    backgroundColor: '#fff',
    marginLeft: 0,
  },
  active: {},
  thumb: {
    height: 12,
    width: 12,
    backgroundColor: '#1a73e8',
    border: '2px solid currentColor',
    marginTop: -3,
    marginLeft: -4,
  },
})(Slider);

const grid = 20;

const getItemStyle = (
  isDragging: boolean,
  draggableStyle: any,
  isActive: boolean,
  isMobile: boolean,
) => ({
  userSelect: 'none',
  padding: '10px 0 10px 18px',
  color: 'rgb(26, 115, 232)',
  margin: `0 0 ${grid}px 0`,
  background: '#fff',
  opacity: !isActive || isDragging ? 0.9 : 0.5,
  borderRadius: 6,
  width: isMobile ? '100%' : 440,
  fontFamily: 'Roboto, sans-serif',
  fontSize: '15px',
  letterSpacing: '0.08px',
  lineHeight: '26px',
  backgroundImage:
    'linear-gradient(rgb(218, 220, 224), rgb(218, 220, 224)), linear-gradient(rgb(218, 220, 224), rgb(218, 220, 224))',
  backgroundSize: '16px 2px, 16px 2px',
  backgroundPosition:
    'calc(100% - 14px) calc(50% + 2px), calc(100% - 14px) calc(50% - 2px)',
  backgroundRepeat: 'no-repeat',
  boxShadow: isDragging
    ? '0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 5px 8px 0px rgb(0 0 0 / 14%), 0px 1px 14px 0px rgb(0 0 0 / 12%)'
    : 'rgb(54 64 67 / 0%) 0px 1px 3px 0px, rgb(54 64 67 / 0%) 0px 4px 8px 3px',

  border: isDragging ? '1px solid #1a73e8' : '1px solid rgb(218, 220, 224)',
  transition:
    'box-shadow 2s ease-in-out 0s, border 0.2s ease-in-out 0s, color 0.2s ease-in-out 0s',

  ...draggableStyle,
});

const getListStyle = (isMobile: boolean) => ({
  padding: `${grid}px 0`,
  width: isMobile ? '100%' : 250,
  position: 'relative' as any,
  zIndex: 100,
});

export interface IBaseQuestion {
  onNext: () => void;
  onPrevious: () => void;
  isPreviousDisabled: boolean;
  animatedImage?: JSX.Element;
  insight?: any;
}

export interface IQuestion {
  headerText: string;
  help: string;
  questionKey: QKey;
  type: 'slider' | 'order' | 'select';
  options?: Array<{ text: string; value: number }>;
}

const Answer: React.FC<{
  type: 'slider' | 'order' | 'select';
  questionKey: QKey;
  options?: Array<{ text: string; value: number }>;
}> = ({ type, options, questionKey }) => {
  const { buttonAnswer, choosenButtonAnswer, selectContainer } = useStyles();
  const { values, setValue } = useAnswerContext();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  const matchesMD = useMediaQuery(theme.breakpoints.up('md'));
  const val = values[questionKey];
  if (type === 'slider') {
    return (
      <CustomSlider
        defaultValue={3}
        aria-labelledby="discrete-slider"
        valueLabelDisplay="auto"
        style={matches ? { margin: '30px 0 10px' } : undefined}
        step={1}
        marks
        min={1}
        max={5}
        value={val}
        onChange={(_, num) => setValue(questionKey, num as number)}
      />
    );
  }
  if (type === 'select' && options) {
    return (
      <div className={selectContainer}>
        {matchesMD ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'stretch',
                flexDirection: 'column',
              }}
            >
              {options.map(({ text, value }, i) =>
                (i + 1) % 2 ? (
                  <Button
                    className={
                      value === val ? choosenButtonAnswer : buttonAnswer
                    }
                    key={value}
                    onClick={() => setValue(questionKey, value)}
                  >
                    {text}
                  </Button>
                ) : null,
              )}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'stretch',
                flexDirection: 'column',
              }}
            >
              {options.map(({ text, value }, i) =>
                i % 2 ? (
                  <Button
                    className={
                      value === val ? choosenButtonAnswer : buttonAnswer
                    }
                    key={value}
                    onClick={() => setValue(questionKey, value)}
                  >
                    {text}
                  </Button>
                ) : null,
              )}
            </div>
          </div>
        ) : (
          options.map(({ text, value }) => (
            <Button
              className={value === val ? choosenButtonAnswer : buttonAnswer}
              key={value}
              onClick={() => setValue(questionKey, value)}
            >
              {text}
            </Button>
          ))
        )}
      </div>
    );
  }
  if (typeof val === 'number' || !options) {
    return null;
  }

  return (
    <DragDropContext
      onDragEnd={(result) => {
        if (!result.destination) {
          return;
        }

        const reorder = (
          list: Array<number>,
          startIndex: number,
          endIndex: number,
        ) => {
          const result = Array.from(list);
          const [removed] = result.splice(startIndex, 1);
          result.splice(endIndex, 0, removed);

          return result;
        };

        const items = reorder(
          val,
          result.source.index,
          result.destination.index,
        );

        setValue('q3', items as any);
      }}
    >
      <Droppable droppableId="droppable">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={getListStyle(matches)}
          >
            {val.map((item, index) => {
              const { draggingOverWith } = snapshot;
              return (
                <Draggable key={item} draggableId={'drag' + item} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style,
                        draggingOverWith !== null,
                        matches,
                      )}
                    >
                      {options.find((x) => x.value === item)?.text}
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export const BaseQuestion: React.FC<IBaseQuestion & IQuestion> = ({
  onNext,
  onPrevious,
  isPreviousDisabled,
  headerText,
  help,
  questionKey,
  animatedImage,
  insight,
  ...rest
}) => {
  const { button, header, info } = useStyles();
  const { values, setValue } = useAnswerContext();
  const val = values[questionKey];
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const tablet = useMediaQuery(theme.breakpoints.up('sm'));
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    setTimeout(() => {
      if (rootRef.current) {
        rootRef.current.style.transform = '';
      }
    }, 1200);
  });

  return (
    <div ref={rootRef}>
      <h2
        className={header}
        style={{ maxWidth: matches && tablet ? 470 : undefined }}
      >
        {headerText}
      </h2>
      <p
        className={info}
        style={{
          lineHeight: '32px',
          paddingTop: '15px',
          paddingRight: matches && tablet ? 150 : 0,
        }}
      >
        {help}
      </p>
      <div>
        <Answer {...rest} questionKey={questionKey} />
      </div>
      {animatedImage}
      <div style={{ display: 'flex' }}>
        {isPreviousDisabled ? null : (
          <Button variant="contained" className={button} onClick={onPrevious}>
            {matches ? 'Previous' : 'Previous question'}
          </Button>
        )}
        <Button
          color="primary"
          variant="contained"
          style={{ flex: matches ? 1 : undefined }}
          onClick={() => {
            setValue(questionKey, val as any);
            onNext();
          }}
          disabled={val < 1}
        >
          {insight ? 'Continue' : matches ? 'Next' : 'Next question'}
        </Button>
      </div>
    </div>
  );
};
